import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';

function BlankCase() {
  return {
    slug: '', number: '05', title: '', tag: '', blurb: '',
    lede: '', problem: '', approach: '', outcome: '',
    stack: [], metric: '', images: [],
    order: 0, published: true,
  };
}

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(String(e.target?.result || ''));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function slugify(s) {
  return String(s || '').toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
}

export default function CaseStudiesAdmin() {
  const [studies, setStudies] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const r = await api.get('/case-studies?include_drafts=true');
    setStudies(r.data);
  };

  useEffect(() => { refresh(); }, []);

  const save = async () => {
    const payload = {
      ...editing,
      slug: (editing.slug || slugify(editing.title)).trim(),
      stack: (typeof editing.stack === 'string' ? editing.stack.split(',').map(s => s.trim()).filter(Boolean) : editing.stack) || [],
      images: (editing.images || []).filter((im) => im.image).map((im) => ({ image: im.image, caption: im.caption || '' })),
      order: Number(editing.order) || 0,
    };
    delete payload._existing;
    if (!payload.title) { toast.error('Title is required.'); return; }
    if (!payload.slug) { toast.error('Slug is required.'); return; }
    setBusy(true);
    try {
      if (editing._existing) {
        await api.put(`/case-studies/${editing._existing}`, payload);
      } else {
        await api.post('/case-studies', payload);
      }
      toast.success('Saved');
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete case study "${slug}"? This removes its detail page too.`)) return;
    await api.delete(`/case-studies/${slug}`);
    toast.success('Deleted');
    refresh();
  };

  const startEdit = (s) => {
    setEditing({
      ...s,
      _existing: s.slug,
      stack: (s.stack || []).join(', '),
      images: s.images || [],
    });
  };

  // --- case study images ---
  const onImageFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const additions = [];
    for (const file of files) {
      if (file.size > 900_000) {
        toast.error(`"${file.name}" is too large (max ~900 KB). Compress it first.`);
        continue;
      }
      const data = await fileToDataURL(file);
      additions.push({ image: data, caption: '' });
    }
    setEditing((prev) => ({ ...prev, images: [...(prev.images || []), ...additions] }));
    e.target.value = '';
  };
  const setImageCaption = (i, val) => {
    setEditing((prev) => {
      const images = [...(prev.images || [])];
      images[i] = { ...images[i], caption: val };
      return { ...prev, images };
    });
  };
  const removeImage = (i) => setEditing((prev) => ({ ...prev, images: (prev.images || []).filter((_, k) => k !== i) }));
  const moveImage = (i, dir) => {
    setEditing((prev) => {
      const images = [...(prev.images || [])];
      const j = i + dir;
      if (j < 0 || j >= images.length) return prev;
      [images[i], images[j]] = [images[j], images[i]];
      return { ...prev, images };
    });
  };
  const addImageUrl = () => setEditing((prev) => ({ ...prev, images: [...(prev.images || []), { image: '', caption: '' }] }));
  const setImageUrl = (i, val) => {
    setEditing((prev) => {
      const images = [...(prev.images || [])];
      images[i] = { ...images[i], image: val };
      return { ...prev, images };
    });
  };

  return (
    <section className="mb-16" data-testid="case-studies-admin">
      <div className="flex items-center justify-between mb-6">
        <h2 className="serif text-3xl text-ink">Selected work · case studies <span className="mono text-sm text-ink/50 ml-3">{studies.length} total</span></h2>
        <button type="button" onClick={() => setEditing(BlankCase())} data-testid="new-case-button" className="bg-void text-paper px-5 py-3 mono uppercase tracking-[0.22em] text-[11px] hover:bg-orchid transition-colors">
          + Add case study
        </button>
      </div>

      <div className="border border-ink/10">
        {studies.map((s) => (
          <div key={s.slug} className="grid grid-cols-12 gap-4 items-center border-b border-ink/10 last:border-0 p-5" data-testid={`admin-case-row-${s.slug}`}>
            <div className="col-span-1 serif text-2xl text-orchid">{s.number}</div>
            <div className="col-span-8">
              <div className="serif text-lg text-ink leading-snug">{s.title}</div>
              <div className="mono text-[11px] text-ink/50 mt-1">/{s.slug} · {s.published ? 'Published' : 'Draft'} · order {s.order}</div>
            </div>
            <div className="col-span-3 flex gap-2 justify-end">
              <button onClick={() => startEdit(s)} className="mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 border border-ink/20 hover:border-orchid">Edit</button>
              <button onClick={() => remove(s.slug)} className="mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 border border-ink/20 hover:border-red-700">Delete</button>
            </div>
          </div>
        ))}
        {studies.length === 0 && <div className="p-8 mono text-sm text-ink/50">No case studies yet.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-void/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto" data-testid="case-editor">
          <div className="w-full max-w-3xl bg-paper border border-ink/20 p-6 md:p-10 my-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-orchid">{editing._existing ? 'Edit case study' : 'New case study'}</div>
              <button onClick={() => setEditing(null)} className="mono text-sm">✕</button>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Title *</label>
                <input value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value, slug: editing._existing ? editing.slug : slugify(e.target.value)})} data-testid="case-title" className="w-full bg-transparent border-b border-ink/30 focus:border-orchid outline-none serif text-xl py-2" />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Slug (URL, immutable after create)</label>
                <input value={editing.slug} onChange={(e) => setEditing({...editing, slug: slugify(e.target.value)})} disabled={!!editing._existing} placeholder="the-matcher" className="w-full bg-transparent border-b border-ink/30 focus:border-orchid outline-none mono text-sm py-2 disabled:opacity-50" />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Number (display)</label>
                <input value={editing.number} onChange={(e) => setEditing({...editing, number: e.target.value})} placeholder="05" className="w-full bg-transparent border-b border-ink/30 outline-none mono text-sm py-2" />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Order</label>
                <input type="number" value={editing.order} onChange={(e) => setEditing({...editing, order: e.target.value})} className="w-full bg-transparent border-b border-ink/30 outline-none mono text-sm py-2" />
              </div>
              <div className="pt-6">
                <label className="mono text-xs flex items-center gap-2">
                  <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({...editing, published: e.target.checked})} />
                  Published
                </label>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Tag (short subtitle)</label>
                <input value={editing.tag} onChange={(e) => setEditing({...editing, tag: e.target.value})} placeholder="62% fit. Rude. Correct." className="w-full bg-transparent border-b border-ink/30 outline-none mono text-sm py-2" />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Metric</label>
                <input value={editing.metric} onChange={(e) => setEditing({...editing, metric: e.target.value})} placeholder="e.g. 214 roles scored" className="w-full bg-transparent border-b border-ink/30 outline-none mono text-sm py-2" />
              </div>
            </div>

            <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Blurb (shown on card)</label>
            <textarea value={editing.blurb} onChange={(e) => setEditing({...editing, blurb: e.target.value})} rows={2} className="w-full bg-transparent border border-ink/20 focus:border-orchid outline-none mono text-sm p-3 mb-4" />

            <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Lede (large italic quote on detail page)</label>
            <textarea value={editing.lede} onChange={(e) => setEditing({...editing, lede: e.target.value})} rows={2} className="w-full bg-transparent border border-ink/20 focus:border-orchid outline-none mono text-sm p-3 mb-4" />

            <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Problem</label>
            <textarea value={editing.problem} onChange={(e) => setEditing({...editing, problem: e.target.value})} rows={3} className="w-full bg-transparent border border-ink/20 focus:border-orchid outline-none mono text-sm p-3 mb-4" />

            <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Approach</label>
            <textarea value={editing.approach} onChange={(e) => setEditing({...editing, approach: e.target.value})} rows={3} className="w-full bg-transparent border border-ink/20 focus:border-orchid outline-none mono text-sm p-3 mb-4" />

            <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Outcome</label>
            <textarea value={editing.outcome} onChange={(e) => setEditing({...editing, outcome: e.target.value})} rows={3} className="w-full bg-transparent border border-ink/20 focus:border-orchid outline-none mono text-sm p-3 mb-4" />

            <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid block mb-1">Stack (comma-separated)</label>
            <input value={editing.stack} onChange={(e) => setEditing({...editing, stack: e.target.value})} placeholder="Claude, Python, Sheets" className="w-full bg-transparent border-b border-ink/30 outline-none mono text-sm py-2 mb-6" />

            {/* Graphics / images */}
            <div className="border border-ink/15 p-4 mb-6" data-testid="case-images-block">
              <div className="flex items-center justify-between mb-2">
                <label className="mono text-[10px] uppercase tracking-[0.3em] text-orchid">Graphics (shown on the case detail page)</label>
                <div className="flex gap-2">
                  <label className="mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border border-ink/20 hover:border-orchid cursor-pointer">
                    + Upload
                    <input type="file" accept="image/*" multiple onChange={onImageFiles} data-testid="case-image-file" className="hidden" />
                  </label>
                  <button type="button" onClick={addImageUrl} className="mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border border-ink/20 hover:border-orchid">+ URL</button>
                </div>
              </div>
              <p className="mono text-[10px] text-ink/50 mb-3">Upload screenshots/mockups (≤ 900 KB each) or paste image URLs. Add a caption to each. Reorder with ↑ ↓.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(editing.images || []).map((im, i) => (
                  <div key={i} className="border border-ink/10 p-2" data-testid={`case-image-${i}`}>
                    {im.image ? (
                      <img src={im.image} alt={im.caption || `graphic ${i + 1}`} className="w-full aspect-video object-cover mb-2" />
                    ) : (
                      <input value={im.image} onChange={(e) => setImageUrl(i, e.target.value)} placeholder="https://image-url…" className="w-full bg-transparent border-b border-ink/30 outline-none mono text-[11px] py-2 mb-2" />
                    )}
                    <input value={im.caption} onChange={(e) => setImageCaption(i, e.target.value)} placeholder="Caption (optional)" className="w-full bg-transparent border-b border-ink/20 focus:border-orchid outline-none mono text-[11px] py-1.5 mb-2" />
                    <div className="flex gap-1 justify-end">
                      <button type="button" onClick={() => moveImage(i, -1)} className="mono text-[10px] px-2 py-1 border border-ink/20 hover:border-orchid">↑</button>
                      <button type="button" onClick={() => moveImage(i, 1)} className="mono text-[10px] px-2 py-1 border border-ink/20 hover:border-orchid">↓</button>
                      <button type="button" onClick={() => removeImage(i)} className="mono text-[10px] px-2 py-1 border border-ink/20 hover:border-red-700">✕</button>
                    </div>
                  </div>
                ))}
                {(editing.images || []).length === 0 && <div className="col-span-full mono text-[11px] text-ink/40 py-2">No graphics yet.</div>}
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="mono text-[11px] uppercase tracking-[0.22em] px-5 py-3 border border-ink/20 hover:border-orchid">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="case-save" className="bg-void text-paper px-6 py-3 mono uppercase tracking-[0.22em] text-[11px] hover:bg-orchid transition-colors disabled:opacity-60">{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
